import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import './ProductSettings.css';

function ProductSettings() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (productId && products.length > 0) {
      loadProduct(productId);
    }
  }, [productId, products]);

  const fetchProducts = async () => {
    try {
      const res = await api.get('/gurus/lance/products');
      const trackingProducts = res.data.filter(p => p.sheet_url);
      setProducts(trackingProducts);
      setLoading(false);
    } catch (err) {
      console.error('Failed to load products:', err);
      setLoading(false);
    }
  };

  const loadProduct = (prodId) => {
    const product = products.find(p => p.id === prodId);
    if (product) {
      setSelectedProduct(product);
      setFormData({
        name: product.name || '',
        sku: product.sku || '',
        trade_type: product.trade_type || '',
        stock_investment_amount: product.stock_investment_amount || '',
        option_investment_amount: product.option_investment_amount || '',
        minimal_stats: product.minimal_stats || false,
        logo_url: product.logo_url || '',
        oct_url: product.oct_url || '',
        sheet_url: product.sheet_url || ''
      });
    }
  };

  const handleProductClick = (product) => {
    navigate(`/admin/product-settings/${product.id}`);
  };

  const handleBackToProducts = () => {
    setSelectedProduct(null);
    setFormData({});
    setError('');
    setSuccess('');
    navigate('/admin/product-settings');
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Prepare update data - only include fields that can be updated
      const updateData = {
        trade_type: formData.trade_type,
        stock_investment_amount: parseFloat(formData.stock_investment_amount),
        option_investment_amount: parseFloat(formData.option_investment_amount),
        minimal_stats: formData.minimal_stats,
        logo_url: formData.logo_url || null,
        oct_url: formData.oct_url || null,
        sheet_url: formData.sheet_url || null
      };

      await api.put(`/admin/products/${selectedProduct.id}`, updateData);

      setSuccess('Product settings saved successfully!');

      // Refresh products list
      await fetchProducts();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to save product:', err);
      setError(err.response?.data?.detail || 'Failed to save product settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="product-settings-loading">Loading...</div>;
  }

  // Show product list
  if (!selectedProduct) {
    return (
      <div className="product-settings">
        <div className="product-settings-header">
          <h1>Product Settings</h1>
          <p>Select a product to view and edit settings</p>
        </div>

        <div className="admin-products-grid">
          {products.map(product => (
            <div
              key={product.id}
              className="product-card-admin"
              onClick={() => handleProductClick(product)}
            >
              {product.logo_url ? (
                <img src={product.logo_url} alt={product.name} className="product-logo-admin" />
              ) : (
                <div className="product-logo-placeholder-admin">
                  {product.name.substring(0, 2).toUpperCase()}
                </div>
              )}
              <h3>{product.name}</h3>
              <p className="product-sku">{product.sku}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Show product settings form
  return (
    <div className="product-settings">
      <div className="product-settings-header">
        <button onClick={handleBackToProducts} className="back-btn">
          ← Back to Products
        </button>
        <h1>{selectedProduct.name}</h1>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <form onSubmit={handleSubmit} className="product-settings-form">
        <div className="settings-section">
          <h2 className="section-title">Basic Information</h2>
          <div className="form-grid">
            <div className="form-field">
              <label>Product Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                disabled
                className="disabled-input"
              />
              <p className="field-hint">Product name cannot be changed</p>
            </div>

            <div className="form-field">
              <label>SKU</label>
              <input
                type="text"
                name="sku"
                value={formData.sku}
                disabled
                className="disabled-input"
              />
              <p className="field-hint">SKU cannot be changed</p>
            </div>

            <div className="form-field">
              <label>Trade Type</label>
              <select
                name="trade_type"
                value={formData.trade_type}
                onChange={handleChange}
              >
                <option value="OPTIONS">OPTIONS</option>
                <option value="STOCKS">STOCKS</option>
                <option value="CREDIT_SPREADS">CREDIT_SPREADS</option>
                <option value="DEBIT_SPREADS">DEBIT_SPREADS</option>
                <option value="IRON_CONDORS">IRON_CONDORS</option>
                <option value="MIXED">MIXED</option>
              </select>
              <p className="field-hint">Type of trades this product contains</p>
            </div>
          </div>
        </div>

        <div className="settings-section">
          <h2 className="section-title">Investment Amounts</h2>
          <div className="form-grid">
            <div className="form-field">
              <label>Stock Investment Amount ($)</label>
              <input
                type="number"
                name="stock_investment_amount"
                value={formData.stock_investment_amount}
                onChange={handleChange}
                step="0.01"
                min="0"
              />
              <p className="field-hint">Default investment per stock trade (used for P/L calculations)</p>
            </div>

            <div className="form-field">
              <label>Option Investment Amount ($)</label>
              <input
                type="number"
                name="option_investment_amount"
                value={formData.option_investment_amount}
                onChange={handleChange}
                step="0.01"
                min="0"
              />
              <p className="field-hint">Default investment per option trade (used for P/L calculations)</p>
            </div>
          </div>
        </div>

        <div className="settings-section">
          <h2 className="section-title">Display Settings</h2>
          <div className="form-grid">
            <div className="form-field checkbox-field">
              <label>
                <input
                  type="checkbox"
                  name="minimal_stats"
                  checked={formData.minimal_stats}
                  onChange={handleChange}
                />
                <span>Show Minimal Stats Only</span>
              </label>
              <p className="field-hint">Only show winners, losers, win rate, and avg hold time</p>
            </div>
          </div>
        </div>

        <div className="settings-section">
          <h2 className="section-title">URLs & Integration</h2>
          <div className="form-grid">
            <div className="form-field full-width">
              <label>Logo URL</label>
              <input
                type="url"
                name="logo_url"
                value={formData.logo_url}
                onChange={handleChange}
              />
              <p className="field-hint">URL to product logo image</p>
            </div>

            <div className="form-field full-width">
              <label>OneClickTrading URL</label>
              <input
                type="url"
                name="oct_url"
                value={formData.oct_url}
                onChange={handleChange}
              />
              <p className="field-hint">Link to product page on OneClickTrading</p>
            </div>

            <div className="form-field full-width">
              <label>Google Sheet Sync URL</label>
              <input
                type="url"
                name="sheet_url"
                value={formData.sheet_url}
                onChange={handleChange}
              />
              <p className="field-hint">CSV export URL from Google Sheets for auto-sync</p>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" onClick={handleBackToProducts} className="cancel-btn" disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="save-btn" disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ProductSettings;
